<template>
  <v-container fluid :class="containerClass">
    <input
      ref="uploader"
      class="d-none"
      type="file"
      @change="fileChanged"/>
    <DataTable
    :items.sync="files"
    :headers="headers"
    :actions="tableActions"
    :customCells="customCells"
    @refresh="loadFiles"
    @upload="uploadFile"
    htmlIdentifier="tract-files">
      <template #name="{item}">
        {{ item.name }}
      </template>
      <template #type="{item}">
        {{ item.type }}
      </template>
      <template #date="{item}">
        {{ dateFromMilliseconds(item.date) }}
      </template>
      <template #actions="{item}">
        <Icon
          icon="mdi-download"
          iconColor="success"
          :tooltipText="$t('downloadFile')"
          @icon-clicked="downloadFile(item)"
          elementId="download"
        />
        <Icon
          v-if="isEditing"
          icon="mdi-delete-forever"
          iconColor="error"
          :tooltipText="$t('deleteTractFileConfirmation')"
          @icon-clicked="deleteBtnClicked(item)"
          elementId="delete"/>
      </template>
      <template #user="{item}">
        {{ userFromId(item.user) }}
      </template>
      <template #footer v-if="isEditing">
        {{fileLimitFooter}}
      </template>
    </DataTable>
    <v-dialog v-model="deleteConfirmation" width="400px">
      <ConfirmDialog
      :title="$t('deleteTractFile')"
      :body="$t('deleteTractFileBody', {filename: fileForDelete.name, tractName: tract.name})"
      :confirmText="$t('deleteTractFileConfirmation')"
      @confirm="deleteFile(fileForDelete)"
      @cancel="deleteConfirmation = false"/>
  </v-dialog>
  </v-container>
</template>
<script>
import TractHeaders from '@/headers/Tract.js'
import { tractFilesClient, validateTractFile } from '@/utils/TractFiles'
import { mapActions } from 'vuex'
import { saveAs } from 'file-saver'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { downscaleImage } from '@/utils/Images'
import { MAX_ATTACHMENT_FILES } from '@/utils/Files'

export default {
  name: 'TractFiles',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue')
  },

  props: {
    isEditing: {
      type: Boolean,
      required: false,
      default: false
    },
    tract: {
      type: Object,
      default: undefined
    }
  },

  data: () => ({
    columnSet: null,
    files: [],
    selectedFile: null,
    deleteConfirmation: false,
    fileForDelete: {
      name: undefined,
      type: undefined,
      ref: undefined
    },
    isSelecting: false,
    users: []
  }),

  computed: {
    headers () {
      return TractHeaders.fileHeaders()
    },

    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh')
        },
        (this.isEditing) ? {
          actionName: 'upload',
          icon: 'mdi-plus',
          text: this.$t('uploadFile'),
          disabled: this.files.length >= MAX_ATTACHMENT_FILES,
          id: 'new-tract-file'
        } : undefined
      ].filter(a => a !== undefined)
    },

    customCells () {
      return [
        'name',
        'type',
        'user',
        'date',
        'actions'
      ].map(n => ({ slotName: n, value: n }))
    },

    fileLimitFooter () {
      return `${this.files.length} / ${MAX_ATTACHMENT_FILES} ${this.$t('files')}`
    },

    containerClass () {
      return this.isEditing ? 'tract-form-files' : 'tract-detail-files'
    }
  },

  async created () {
    this.loadFiles()
    this.users = await this.getAllUsers()
  },

  methods: {
    ...mapActions('user', ['getAllUsers']),
    async loadFiles () {
      const loadedFiles = await tractFilesClient.getFiles(this.tract)
      this.files = loadedFiles
    },

    async uploadToBlob () {
      if (!this.selectedFile) return
      if (!this.validateUpload(this.selectedFile)) return
      await tractFilesClient.uploadFile(this.tract, this.selectedFile)
      this.loadFiles()
      this.selectedFile = undefined
    },

    async deleteFile (file) {
      await tractFilesClient.deleteFile(file.ref)
      this.loadFiles()
      this.deleteConfirmation = false
    },

    async downloadFile (file) {
      const fetchedFile = await fetch(file.ref)
      const blob = await fetchedFile.blob()
      saveAs(blob, decodeURIComponent(fetchedFile.url.split('?')[0].split('/').pop()))
    },

    deleteBtnClicked (file) {
      this.deleteConfirmation = true
      this.fileForDelete = file
    },

    uploadFile () {
      this.isSelecting = true
      window.addEventListener('focus', () => {
        this.isSelecting = false
      }, { once: true })

      this.$refs.uploader.click()
    },

    async fileChanged (e) {
      const picked = e.target.files[0]
      this.selectedFile = picked?.type.startsWith('image/') ? await downscaleImage(picked) : picked
      this.uploadToBlob()
      this.isSelecting = false
      this.$refs.uploader.value = ''
    },

    validateUpload (file) {
      const error = validateTractFile(file)
      if (error) {
        this.setSnackError(error)
        this.selectedFile = undefined
        return false
      }
      return true
    },

    dateFromMilliseconds (milliseconds) {
      const d = new Date(+milliseconds)
      return utcToLocalDate(d)
    },

    userFromId (id) {
      return this.users.find(u => u.applicationUserId.toString() === id.toString())?.name
    }
  }
}
</script>
